import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Security } from '../../../../proto/generated/security'
import { ColorType, createChart, IChartApi, ISeriesApi, PriceScaleMode, Time } from 'lightweight-charts'
import { formatLargeNumber } from '../../../../util/screener'
import { GroupedBarsSeries } from './barseries/grouped-bars-series'
import { CrosshairHighlightPrimitive } from './barseries/example/highlight-bar-crosshair'
import { Typography, useTheme } from '@mui/material'
import { chartLayoutOptions } from '../../../../assets/constants'
import OiChartMetaData from './oiChartMetaData'
import { VertLine } from './barseries/verticalLine'
import { useResizeDetector } from 'react-resize-detector'
import ChartSettingsButtons from '../../../chartV2/chartContextMenuButton/ChartSettingsButtons'
import { AppContext } from '../../../../App'

export interface OiWidgetDataType {
    [key: string]: number | number[] | undefined

    time: number | undefined
    values: number[]
}

type Props = {
    chartId: string
    loading: boolean
    allowSplit?: boolean,
    data: OiWidgetDataType[]
    atmStrike: number | undefined
    security: Security
    expiryName: number
    expiryMenu: {
        value: string
        label: string
    }[]
    selectedExpiry: {
        expirySeconds: string
        expiry: string
    } | undefined
    setSelectedExpiry: React.Dispatch<React.SetStateAction<{
        expirySeconds: string;
        expiry: string;
    } | undefined>>
}

const OiChartNew = (props: Props) => {
    const {
        chartId,
        loading,
        security,
        data,
        expiryName,
        expiryMenu,
        selectedExpiry,
        setSelectedExpiry,
        atmStrike,
        allowSplit
    } = props
    const { width, height, ref } = useResizeDetector()
    const appContext = useContext(AppContext)
    const chartRef = useRef<IChartApi | null>(null)
    const iChartSeriesApi = useRef<ISeriesApi<any> | null>(null)
    const memoizedChartId = useMemo(() => chartId, [chartId])
    const memoizedSecurity = useMemo(() => security, [security])
    const theme = useTheme()
    const [hoveredData, setHoveredData] = useState<{
        strike: number
        calloi: number
        putOi: number
    }>()
    const [callOiAtm, putOiAtm] = useMemo(() => {
        const foundData = data.find(d => d.time === Number(atmStrike))
        return [foundData?.values?.[0] ?? 0, foundData?.values?.[1] ?? 0]
    }, [data, atmStrike])

    const chartContainerElement = useMemo(() => {
        return document.getElementById(chartId)
    }, [chartId])
    const renderedFirstTime = useRef(false)
    const atmTagRef = useRef<HTMLDivElement | null>(null)

    const layoutOptions = useMemo(() => {
        return {
            ...chartLayoutOptions,
            height: height,
            width: width,
            layout: {
                background: {
                    type: ColorType.Solid,
                    color: theme.palette.background.paper
                },
                fontFamily: `'Work Sans', sans-serif`,
                textColor: theme.palette.text.primary
            },
            grid: {
                horzLines: {
                    color: '#88888815'
                },
                vertLines: {
                    color: '#88888815'
                }
            },
            timeScale: {
                borderVisible: false,
                borderColor: theme.palette.divider
            },
            rightPriceScale: {
                borderVisible: false,
                alignLabels: true,
                borderColor: theme.palette.divider,
                visible: true,
                mode: PriceScaleMode.Normal
            }
        }
    }, [theme, height, width])

    const onCrossHairMove = useCallback((e: any) => {
        if (iChartSeriesApi.current) {
            const oiArray = e.seriesData.get(iChartSeriesApi.current)?.values as number[]
            setHoveredData({
                strike: e?.time as number,
                calloi: oiArray?.[0] ?? 0,
                putOi: oiArray?.[1] ?? 0
            })
        }
        if (chartRef.current && atmTagRef.current && atmStrike) {
            const value = Math.min(Math.max(0, (chartRef.current?.timeScale().timeToCoordinate(atmStrike as Time) ?? 0) - 13), (width ?? 0) - 27 - chartRef.current.priceScale('right').width())
            atmTagRef.current.style.left = `${value}px`
        }
    }, [atmStrike, width])

    useEffect(() => {
        if (!renderedFirstTime.current) {
            renderedFirstTime.current = true
            chartRef.current = (window as unknown as any).chart = createChart(chartId, {
                width: chartContainerElement?.clientWidth,
                height: chartContainerElement?.clientWidth,
                localization: {
                    timeFormatter: (time: any) => {
                        return time.toString()
                    },
                    priceFormatter: (price: any) => {
                        return formatLargeNumber(price, 2)
                    }
                },
                timeScale: {
                    tickMarkFormatter: (time: any) => {
                        return time.toString()
                    },
                    fixLeftEdge: true,
                    fixRightEdge: true,
                    borderVisible: false
                }
            })
            appContext.setOiChart(chartId, chartRef.current)

            chartRef.current.applyOptions(layoutOptions)

            const customSeriesView = new GroupedBarsSeries()

            iChartSeriesApi.current = chartRef.current.addCustomSeries(customSeriesView as any, {
                color: 'black'
            })
            if (iChartSeriesApi.current) {
                iChartSeriesApi.current.attachPrimitive(
                    new CrosshairHighlightPrimitive({ color: 'rgba(0, 100, 200, 0.2)' }) as any
                )
                iChartSeriesApi.current.applyOptions({
                    lastValueVisible: false,
                    priceLineVisible: false
                })
            }
            const priceScale = chartRef.current.priceScale('right')

            priceScale.applyOptions({
                // autoScale: false,
                scaleMargins: {
                    top: 0.1,
                    bottom: 0
                }
            })
        }
        chartRef.current && chartRef.current.subscribeCrosshairMove(onCrossHairMove)
        return () => {
            chartRef.current && chartRef.current?.unsubscribeCrosshairMove(onCrossHairMove)
        }
    }, [onCrossHairMove])

    useEffect(() => {
        chartRef.current && chartRef.current.applyOptions(layoutOptions)
    }, [layoutOptions])

    useEffect(() => {
        if (iChartSeriesApi.current) {
            iChartSeriesApi.current.setData(data as any)
            const index = data.findIndex(d => d.time === atmStrike)
            if (index >= 0) {
                const from = data[Math.max(index - 10, 0)]
                const to = data[Math.min(index + 10, data.length - 1)]
                chartRef.current?.timeScale().setVisibleRange({
                    from: from.time as Time,
                    to: to.time as Time
                })
            }
        }
        let vertLine: VertLine
        if (chartRef.current && iChartSeriesApi.current) {
            vertLine = new VertLine(chartRef.current, iChartSeriesApi.current, (atmStrike ?? 0) as Time, {
                showLabel: false,
                color: theme.palette.primary.main
            })
            iChartSeriesApi.current.attachPrimitive(vertLine)
        }
        return () => {
            if (vertLine) {
                iChartSeriesApi.current?.detachPrimitive(vertLine)
            }
        }
    }, [atmStrike, data, theme])

    useEffect(() => {
        if (chartRef.current && atmTagRef.current && atmStrike) {
            const value = Math.min(Math.max(0, (chartRef.current?.timeScale().timeToCoordinate(atmStrike as Time) ?? 0) - 13), (width ?? 0) - 27 - chartRef.current.priceScale('right').width())
            atmTagRef.current.style.left = `${value}px`
        }
    }, [atmStrike, data, width])

    const handleMouseLeave = useCallback(() => {
        const foundData = data.find(d => d.time === Number(atmStrike))
        setHoveredData({
            strike: atmStrike ?? 0,
            calloi: foundData?.values[0] ?? 0,
            putOi: foundData?.values[1] ?? 0
        })
    }, [atmStrike, data])

    return (
        <>
            <div id={chartId} style={{
                height: '100%',
                widows: '100%',
                margin: '0px',
                padding: '0px',
                position: 'relative',
                filter: loading ? 'blur(12px)' : 'none'
            }} onMouseLeave={handleMouseLeave} ref={ref} />
            <Typography ref={atmTagRef} style={{
                color: theme.palette.primary.main,
                padding: '0 2px',
                fontSize: '10px',
                border: `1px solid ${theme.palette.primary.main}`,
                borderRadius: '4px',
                position: 'absolute',
                background: theme.palette.background.default,
                textWrap: 'nowrap',
                bottom: '30px',
                zIndex: 10,
                visibility: atmStrike && !loading ? 'visible' : 'hidden'
            }}>
                ATM
            </Typography>
            <ChartSettingsButtons allowSplit={allowSplit} viewKey={memoizedChartId} security={memoizedSecurity} />
            <OiChartMetaData
                security={security}
                expiryName={expiryName}
                expiryMenu={expiryMenu}
                selectedExpiry={selectedExpiry}
                setSelectedExpiry={setSelectedExpiry}
                hoveredStrike={hoveredData?.strike ?? (atmStrike ?? 0)}
                callOi={formatLargeNumber((hoveredData?.strike ? hoveredData?.calloi ?? 0 : callOiAtm), 2)}
                putOi={formatLargeNumber((hoveredData?.strike ? hoveredData?.putOi ?? 0 : putOiAtm), 2)}
            />
        </>
    )
}

export default OiChartNew